// Lazy Monaco panes for Docs Hub: the markdown source editor and the
// revision-compare diff view. Both are code-split (`lazy(() => import(...))`)
// so the ~1 MB editor bundle only loads when a user actually edits or compares
// — the viewer path never pays for it. The Monaco loader itself is configured
// once at app boot in `lib/monaco-editor-initializer`, mirroring the dashboards
// editor.

import { Suspense, lazy } from 'react'
import { Loader2 } from 'lucide-react'
import { useSettings } from '@axilon-apps/libs-ui-dashboard'
import { designPixelsToScaledPixels } from '../../lib/ui-density'

const MonacoEditor = lazy(() => import('@monaco-editor/react'))
const MonacoDiffEditor = lazy(() =>
  import('@monaco-editor/react').then((mod) => ({ default: mod.DiffEditor }))
)

const editorOptions = () =>
  ({
    fontSize: designPixelsToScaledPixels(13),
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    automaticLayout: true,
    tabSize: 2,
    wordWrap: 'on'
  }) as const

const Fallback = ({ label }: { label: string }) => (
  <div className="flex h-full items-center justify-center gap-2 text-sm text-ink-3">
    <Loader2 className="size-4 animate-spin" aria-hidden />
    {label}
  </div>
)

function useMonacoTheme(): string {
  const { settings } = useSettings()
  return settings.colorScheme === 'dark' ? 'vs-dark' : 'vs'
}

export interface LazyMarkdownEditorProps {
  value: string
  onChange: (value: string) => void
}

export const LazyMarkdownEditor = ({ value, onChange }: LazyMarkdownEditorProps) => {
  const theme = useMonacoTheme()
  return (
    <Suspense fallback={<Fallback label="Loading editor…" />}>
      <MonacoEditor
        height="100%"
        defaultLanguage="markdown"
        language="markdown"
        path="doc.md"
        theme={theme}
        value={value}
        onChange={(next) => onChange(next ?? '')}
        options={editorOptions()}
      />
    </Suspense>
  )
}

export interface LazyMarkdownDiffProps {
  /** The older side — the revision being compared. */
  original: string
  /** The newer side — usually the doc's current content. */
  modified: string
}

export const LazyMarkdownDiff = ({ original, modified }: LazyMarkdownDiffProps) => {
  const theme = useMonacoTheme()
  return (
    <Suspense fallback={<Fallback label="Loading diff…" />}>
      <MonacoDiffEditor
        height="100%"
        language="markdown"
        original={original}
        modified={modified}
        theme={theme}
        options={{ ...editorOptions(), readOnly: true, renderSideBySide: true }}
      />
    </Suspense>
  )
}
